import React, { useState, useEffect, useCallback, useMemo } from "react";
import BrandLoader from "../../common/components/BrandLoader";
import Invoke from "../../common/utils/invoke";
import BlogDetailPage from "../blogDetailPage/BlogDetailPage";
import "./BlogDetails.scss";

/** Number of cards per page (per category tab) */
const CARDS_PER_PAGE = 9;

/** Hard-coded filter options for the dropdown */
const FILTER_OPTIONS = ["Latest Additions", "Most Viewed"];

/** Placeholder image when blog has no image */
const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='220' fill='%23f0f0f0'%3E%3Crect width='400' height='220'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23aaa' font-size='18'%3ENo Image%3C/text%3E%3C/svg%3E";

/**
 * Attempts to match the current page URL against a blog's blogFriendlyURL.
 * Returns the matched blog object, or null if we're on the landing page.
 */
function findMatchingBlog(blogs) {
  const currentHref = window.location.href.toLowerCase();
  const currentPath = window.location.pathname.toLowerCase();

  return (
    blogs.find((blog) => {
      if (!blog.blogFriendlyURL) return false;
      const friendly = blog.blogFriendlyURL.toLowerCase().trim();
      if (!friendly) return false;

      // 1. Exact href or pathname match
      if (currentHref === friendly || currentPath === friendly) return true;

      // 2. If friendly is a full URL, extract its pathname and compare
      try {
        const fUrl = new URL(friendly);
        if (fUrl.pathname.toLowerCase() === currentPath) return true;
      } catch {
        /* not a full URL – continue */
      }

      // 3. Check if current path ends with the friendly slug
      //    (e.g. path="/web/guest/blog/5-red-flags", friendly="/blog/5-red-flags")
      if (currentPath.endsWith(friendly)) return true;

      // 4. Check if friendly ends with current path
      //    (e.g. friendly="https://site.com/web/guest/blog/5-red-flags", path="/web/guest/blog/5-red-flags")
      if (friendly.endsWith(currentPath) && currentPath.length > 1) return true;

      return false;
    }) || null
  );
}

const BlogDetails = () => {
  // ─── State ──────────────────────────────────────────────
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchedBlog, setMatchedBlog] = useState(null); // null = landing, object = detail

  // Listing-specific state
  const [activeCategory, setActiveCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("Latest Additions");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [sharePopup, setSharePopup] = useState({ open: false, blog: null });

  // ─── Fetch all blogs on mount & detect page mode ───────
  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const response = await Invoke.fetchBlogsMasters({ pageSize: 200 });
        if (response?.items && Array.isArray(response.items)) {
          setBlogs(response.items);

          // Detect if current URL matches any blog → detail mode
          const matched = findMatchingBlog(response.items);
          if (matched) {
            setMatchedBlog(matched);
          }
        }
      } catch {
        // silently fail – blogs stays []
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // ─── Derive unique categories (preserve order of appearance) ──
  const categories = useMemo(() => {
    const seen = new Set();
    const cats = [];
    blogs.forEach((b) => {
      const cat = b.blogCategory;
      if (cat && !seen.has(cat)) {
        seen.add(cat);
        cats.push(cat);
      }
    });
    return cats;
  }, [blogs]);

  // Auto-select first category once loaded
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  // ─── Filter & sort blogs for active tab ──────────────────
  const filteredBlogs = useMemo(() => {
    let filtered = blogs.filter((b) => b.blogCategory === activeCategory);

    if (activeFilter === "Latest Additions") {
      filtered = [...filtered].sort((a, b) => {
        const dateA = parseBlogDate(a.blogPublishDate);
        const dateB = parseBlogDate(b.blogPublishDate);
        return dateB - dateA;
      });
    }

    if (activeFilter === "Most Viewed") {
      filtered = [...filtered].sort((a, b) => {
        return (b.blogViewCounts || 0) - (a.blogViewCounts || 0); // highest views first
      });
    }

    return filtered;
  }, [blogs, activeCategory, activeFilter]);

  // ─── Pagination ──────────────────────────────────────────
  const totalPages = Math.max(
    1,
    Math.ceil(filteredBlogs.length / CARDS_PER_PAGE),
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, activeFilter]);

  const paginatedBlogs = useMemo(() => {
    const start = (currentPage - 1) * CARDS_PER_PAGE;
    return filteredBlogs.slice(start, start + CARDS_PER_PAGE);
  }, [filteredBlogs, currentPage]);

  // ─── Handlers ────────────────────────────────────────────
  const handleTabClick = useCallback((cat) => {
    setActiveCategory(cat);
  }, []);

  const handleFilterToggle = useCallback(() => {
    setFilterDropdownOpen((prev) => !prev);
  }, []);

  const handleFilterSelect = useCallback((filter) => {
    setActiveFilter(filter);
    setFilterDropdownOpen(false);
  }, []);

  /** Increment view count via PATCH and then redirect.
   *  Uses an <a> tag so Ctrl+click / middle-click are handled natively
   *  by the browser (opens background tab without stealing focus).
   *  For a plain click we preventDefault and navigate ourselves.
   */
  const handleReadMore = useCallback(async (blog, e) => {
    const newCount = (blog.blogViewCounts || 0) + 1;

    // For normal click, prevent the <a> default and navigate manually
    // For Ctrl/Meta+click, let the browser handle it natively (background tab)
    if (e && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }

    try {
      if (blog.id) {
        await Invoke.updateBlogViewCount(blog.id, newCount);
      }

      setBlogs((prev) =>
        prev.map((b) =>
          b.id === blog.id ? { ...b, blogViewCounts: newCount } : b,
        ),
      );
    } catch {
      // still navigate even if count update fails
    }

    // Only navigate in-page for normal clicks (Ctrl+click is already handled by the browser)
    if (e && !e.ctrlKey && !e.metaKey && blog.blogFriendlyURL) {
      window.location.href = blog.blogFriendlyURL;
    }
  }, []);

  const handleShareOpen = useCallback((blog) => {
    setSharePopup({ open: true, blog });
  }, []);

  const handleShareClose = useCallback(() => {
    setSharePopup({ open: false, blog: null });
  }, []);

  const handleShareClick = useCallback((url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const handlePageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages],
  );

  // ─── Render: Loading ───────────────────────────────────
  if (loading) {
    return (
      <div className="loader-overlay">
        <BrandLoader color="#8b1a1a" size={12} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // DETAIL PAGE MODE – URL matched a blog's friendlyURL
  // ═══════════════════════════════════════════════════════════
  if (matchedBlog) {
    return (
      <BlogDetailPage
        currentBlog={matchedBlog}
        allBlogs={blogs}
        setAllBlogs={setBlogs}
        setCurrentBlog={setMatchedBlog}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════
  // LANDING PAGE MODE – blog listing with tabs, cards, pagination
  // ═══════════════════════════════════════════════════════════
  return (
    <div className="blog-details">
      {/* ── Top bar: tabs + filter ── */}
      <div className="blog-details__top-bar">
        <div className="blog-details__tabs">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`blog-details__tab ${activeCategory === cat ? "active" : ""}`}
              onClick={() => handleTabClick(cat)}
              type="button"
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="blog-details__filter-wrapper">
          <button
            className="blog-details__filter-btn"
            onClick={handleFilterToggle}
            type="button"
          >
            {activeFilter}
            <span
              className={`blog-details__filter-arrow  ${filterDropdownOpen ?? "open"} `}
            >
              <svg
                width="9"
                height="5"
                viewBox="0 0 9 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.600098 0.599609L4.1001 4.09961L7.6001 0.599609"
                  stroke="#151B32"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </button>

          {filterDropdownOpen && (
            <ul className="blog-details__filter-dropdown">
              {FILTER_OPTIONS.map((opt) => (
                <li
                  key={opt}
                  className={`blog-details__filter-option ${activeFilter === opt ? "active" : ""}`}
                  onClick={() => handleFilterSelect(opt)}
                >
                  {opt}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── Cards grid ── */}
      {paginatedBlogs.length === 0 ? (
        <div className="blog-details__empty">
          No blogs found in this category.
        </div>
      ) : (
        <div className="blog-details__grid">
          {paginatedBlogs.map((blog, idx) => (
            <div className="blog-card" key={`${blog.blogFriendlyURL}-${idx}`}>
              {/* Card image + nudge badge */}
              <div className="blog-card__image-wrapper">
                <img
                  className="blog-card__image"
                  src={
                    blog.blogCardImageURL ||
                    blog.blogBannerImageURL ||
                    PLACEHOLDER_IMG
                  }
                  alt={blog.blogTitle || "Blog"}
                />
                {blog.blogNudge && (
                  <span className="blog-card__nudge">{blog.blogNudge}</span>
                )}
              </div>

              {/* Card body */}
              <div className="blog-card__body">
                <h6 className="blog-card__title large-body semibold Secondary-Grey-Shades-900 m-0">
                  {blog.blogTitle}
                </h6>

                <p className="blog-card__subtitle Secondary-Grey-Shades-900 small-body regular">
                  {blog.blogSubTitle}{" "}
                  <a
                    className="blog-card__read-more small-body regular"
                    href={blog.blogFriendlyURL || "#"}
                    onClick={(e) => handleReadMore(blog, e)}
                  >
                    Read More
                  </a>
                </p>

                <div className="blog-card__meta">
                  <div className="blog-card__discrip">
                    <span className="blog-card__date small-body regular Secondary-Grey-tint-400">
                      <span className="blog-card__icon blog-card__icon--calendar" />
                      {blog.blogPublishDate}
                    </span>
                    <span className="blog-card__views small-body regular Secondary-Grey-tint-400">
                      <span className="blog-card__icon blog-card__icon--eye" />
                      {blog.blogViewCounts || 0} views
                    </span>
                  </div>
                  <button
                    className="blog-card__share-btn"
                    onClick={() => handleShareOpen(blog)}
                    type="button"
                    aria-label="Share"
                  >
                    <ShareIcon />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="blog-details__pagination">
          <button
            className="blog-details__page-btn blog-details__page-btn--prev"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            type="button"
            aria-label="Previous page"
          >
            ‹
          </button>

          {generatePageNumbers(currentPage, totalPages).map((page, idx) =>
            page === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className="blog-details__page-ellipsis"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                className={`blog-details__page-btn ${currentPage === page ? "blog-details__page-btn--active" : ""}`}
                onClick={() => handlePageChange(page)}
                type="button"
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            ),
          )}

          <button
            className="blog-details__page-btn blog-details__page-btn--next"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            type="button"
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}

      {/* ── Share Popup ── */}
      {sharePopup.open && sharePopup.blog && (
        <div className="share-popup__overlay" onClick={handleShareClose}>
          <div className="share-popup" onClick={(e) => e.stopPropagation()}>
            <button
              className="share-popup__close"
              onClick={handleShareClose}
              type="button"
              aria-label="Close"
            >
              ×
            </button>

            <h4 className="share-popup__title">Share</h4>
            <p className="share-popup__subtitle">
              Choose how you&apos;d like to share this.
            </p>

            <ul className="share-popup__list">
              <li
                className="share-popup__item"
                onClick={() =>
                  handleShareClick(sharePopup.blog.blogFacebookShareURL)
                }
              >
                <span className="share-popup__icon share-popup__icon--facebook" />
                <span>Facebook</span>
              </li>
              <li
                className="share-popup__item"
                onClick={() =>
                  handleShareClick(sharePopup.blog.blogLinkedInShareURL)
                }
              >
                <span className="share-popup__icon share-popup__icon--linkedin" />
                <span>LinkedIn</span>
              </li>
              <li
                className="share-popup__item"
                onClick={() =>
                  handleShareClick(sharePopup.blog.blogInstagramShareURL)
                }
              >
                <span className="share-popup__icon share-popup__icon--instagram" />
                <span>Instagram</span>
              </li>
              <li
                className="share-popup__item"
                onClick={() =>
                  handleShareClick(sharePopup.blog.blogGoogleShareURL)
                }
              >
                <span className="share-popup__icon share-popup__icon--google" />
                <span>Google</span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Helpers ────────────────────────────────────────────────

/**
 * Generate an array of page numbers (with "..." ellipsis) for the pagination bar.
 * Always shows first, last, current, and 1 neighbour on each side.
 * E.g. [1, "...", 4, 5, 6, "...", 10]
 */
function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push("...");
    }
    result.push(sorted[i]);
  }
  return result;
}

/**
 * Parse blog date string "Day, DD.MM.YYYY" → Date object.
 * Falls back to 0 (epoch) when format is unrecognised.
 */
function parseBlogDate(dateStr) {
  if (!dateStr) return 0;
 
  //  Format 1: DD.MM.YYYY (12.12.2025)
  const dotMatch = dateStr.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (dotMatch) {
    return new Date(
      Number(dotMatch[3]),
      Number(dotMatch[2]) - 1,
      Number(dotMatch[1])
    ).getTime();
  }
 
  // Format 2: "Wednesday, 22 April 2026"
  try {
    const cleaned = dateStr.replace(/^[A-Za-z]+,\s*/, "").trim();
    return new Date(cleaned).getTime();
  } catch {
    return 0;
  }
}

/** Simple share (arrow) SVG icon */
const ShareIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#8b1a1a"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

export default BlogDetails;
